import { useMemo, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react'
import { niceScale, shortDay, useWidth } from './utils'

export interface ChartSeries { key: string; label: string; color: string }
export type Day = { day: string } & Record<string, number | string>

const pad = { top: 16, right: 12, bottom: 26, left: 44 }
const value = (d: Day, key: string) => Number(d[key]) || 0

/** Hover / arrow-key day picking shared by both charts. */
function useDayPicker(count: number) {
  const [hover, setHover] = useState<number | null>(null)
  const pick = (event: PointerEvent<SVGRectElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const i = Math.floor(((event.clientX - rect.left) / rect.width) * count)
    setHover(Math.max(0, Math.min(count - 1, i)))
  }
  const onKey = (event: KeyboardEvent<SVGSVGElement>) => {
    if (event.key === 'ArrowRight') setHover(h => Math.min(count - 1, (h ?? -1) + 1))
    else if (event.key === 'ArrowLeft') setHover(h => Math.max(0, (h ?? count) - 1))
    else if (event.key === 'Escape') setHover(null)
    else return
    event.preventDefault()
  }
  return { hover, setHover, pick, onKey }
}

function Tooltip({ x, width, title, rows }: { x: number; width: number; title: string; rows: ReactNode }) {
  return <div role="status" className="pointer-events-none absolute top-1 z-10 min-w-40 rounded-lg border bg-popover/95 px-3 py-2 text-xs shadow-card backdrop-blur"
    style={{ left: Math.min(Math.max(0, x - 80), Math.max(0, width - 172)) }}>
    <p className="mb-1 font-medium">{title}</p>{rows}
  </div>
}

function TooltipRow({ color, label, children }: { color: string; label: string; children: ReactNode }) {
  return <p className="flex items-center justify-between gap-4">
    <span className="flex items-center gap-2 text-muted-foreground"><span className="h-2 w-2 rounded-full" style={{ background: color }} />{label}</span>
    <span className="font-medium tabular-nums">{children}</span>
  </p>
}

function Axis({ ticks, y, plotW, format }: { ticks: number[]; y: (v: number) => number; plotW: number; format: (v: number) => string }) {
  return <>{ticks.map(tick => <g key={tick}>
    <line x1={pad.left} x2={pad.left + plotW} y1={y(tick)} y2={y(tick)} stroke="hsl(var(--border))" strokeDasharray={tick ? '2 4' : undefined} />
    <text x={pad.left - 8} y={y(tick)} dy="0.32em" textAnchor="end" className="fill-muted-foreground text-[11px] tabular-nums">{format(tick)}</text>
  </g>)}</>
}

function DayLabels({ data, x, plotW, height }: { data: Day[]; x: (i: number) => number; plotW: number; height: number }) {
  // Spacing is measured in pixels: the last day is always labelled, and no label sits within 56px of it.
  const every = Math.max(1, Math.ceil(data.length / Math.max(2, Math.floor(plotW / 72))))
  const last = data.length - 1
  return <>{data.map((d, i) => (i % every === 0 && x(last) - x(i) >= 56) || i === last
    ? <text key={d.day} x={x(i)} y={height - 6} textAnchor={i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle'}
      className="fill-muted-foreground text-[11px]">{shortDay(d.day).replace(/^\w+, /, '')}</text>
    : null)}</>
}

/**
 * Lines over days on one shared axis (one series, or several in the same unit), each over a soft
 * area; crosshair tooltip, arrow keys read each day.
 */
export function AreaChart({ data, series, format, tooltipFormat = format, height = 240, label, extra }: {
  data: Day[]; series: ChartSeries | ChartSeries[]; format: (v: number) => string; tooltipFormat?: (v: number) => string
  height?: number; label: string; extra?: (d: Day) => ReactNode
}) {
  const list = Array.isArray(series) ? series : [series]
  const [ref, width] = useWidth<HTMLDivElement>()
  const { hover, setHover, pick, onKey } = useDayPicker(data.length)
  const plotW = Math.max(0, width - pad.left - pad.right)
  const plotH = height - pad.top - pad.bottom
  const scale = niceScale(Math.max(0, ...data.flatMap(d => list.map(s => value(d, s.key)))))
  const x = (i: number) => pad.left + (data.length > 1 ? (i / (data.length - 1)) * plotW : plotW / 2)
  const y = (v: number) => pad.top + plotH - (v / scale.max) * plotH
  const keys = list.map(s => s.key).join(',')
  const paths = useMemo(() => list.map(s => {
    const pts = data.map((d, i) => [x(i), y(value(d, s.key))] as const)
    // Horizontal control points never overshoot between days, so a zero day stays on the baseline.
    const line = pts.map(([px, py], i) => {
      if (!i) return `M${px},${py}`
      const [qx, qy] = pts[i - 1]
      const mid = (px + qx) / 2
      return `C${mid},${qy} ${mid},${py} ${px},${py}`
    }).join('')
    const base = pad.top + plotH
    return { s, line, area: pts.length ? `${line}L${pts.at(-1)![0]},${base}L${pts[0][0]},${base}Z` : '' }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [data, width, scale.max, keys])
  const d = hover === null ? null : data[hover]
  const fade = list.length > 1 ? 0.12 : 0.24
  return <div ref={ref} className="relative">
    {width > 0 && <svg width={width} height={height} tabIndex={0} onKeyDown={onKey} onBlur={() => setHover(null)}
      role="img" aria-label={`${label}. Use left and right arrow keys to read each day.`}
      className="overflow-visible rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <defs>{list.map(s => <linearGradient key={s.key} id={`area-${s.key}`} x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor={s.color} stopOpacity={fade} /><stop offset="100%" stopColor={s.color} stopOpacity={0} />
      </linearGradient>)}</defs>
      <Axis ticks={scale.ticks} y={y} plotW={plotW} format={format} />
      <DayLabels data={data} x={x} plotW={plotW} height={height} />
      {paths.map(p => <path key={`a-${p.s.key}-${data.length}-${scale.max}`} d={p.area} fill={`url(#area-${p.s.key})`} className="viz-fade" />)}
      {paths.map(p => <path key={`l-${p.s.key}-${data.length}-${scale.max}`} d={p.line} fill="none" stroke={p.s.color} strokeWidth={2} strokeLinecap="round" pathLength={1} className="viz-draw" />)}
      {d && hover !== null && <g pointerEvents="none">
        <line x1={x(hover)} x2={x(hover)} y1={pad.top} y2={pad.top + plotH} stroke="hsl(var(--foreground))" strokeOpacity={0.25} />
        {list.map(s => <circle key={s.key} cx={x(hover)} cy={y(value(d, s.key))} r={5} fill={s.color} stroke="hsl(var(--card))" strokeWidth={2} />)}
      </g>}
      <rect x={pad.left} y={pad.top} width={plotW} height={plotH} fill="transparent"
        onPointerMove={pick} onPointerDown={pick} onPointerLeave={() => setHover(null)} />
    </svg>}
    {d && hover !== null && <Tooltip x={x(hover)} width={width} title={shortDay(d.day)}
      rows={<>{list.map(s => <TooltipRow key={s.key} color={s.color} label={s.label}>{tooltipFormat(value(d, s.key))}</TooltipRow>)}{extra?.(d)}</>} />}
  </div>
}

/**
 * Daily bars for one or more series: stacked when they are parts of one total (artist uploads +
 * catalog imports), grouped when they are separate counts (tickets opened vs resolved).
 */
export function BarChart({ data, series, mode = 'stacked', format = v => v.toLocaleString(), tooltipFormat = format, height = 200, label }: {
  data: Day[]; series: ChartSeries[]; mode?: 'stacked' | 'grouped'; format?: (v: number) => string; tooltipFormat?: (v: number) => string
  height?: number; label: string
}) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const { hover, setHover, pick, onKey } = useDayPicker(data.length)
  const plotW = Math.max(0, width - pad.left - pad.right)
  const plotH = height - pad.top - pad.bottom
  const top = Math.max(0, ...data.map(d => mode === 'stacked'
    ? series.reduce((n, s) => n + value(d, s.key), 0)
    : Math.max(...series.map(s => value(d, s.key)))))
  const scale = niceScale(top)
  const slot = data.length ? plotW / data.length : 0
  const barW = Math.max(2, Math.min(28, slot * 0.7))
  const y = (v: number) => pad.top + plotH - (v / scale.max) * plotH
  const center = (i: number) => pad.left + slot * (i + 0.5)
  const d = hover === null ? null : data[hover]
  return <div ref={ref} className="relative">
    {width > 0 && <svg width={width} height={height} tabIndex={0} onKeyDown={onKey} onBlur={() => setHover(null)}
      role="img" aria-label={`${label}. Use left and right arrow keys to read each day.`}
      className="overflow-visible rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <Axis ticks={scale.ticks} y={y} plotW={plotW} format={format} />
      <DayLabels data={data} x={center} plotW={plotW} height={height} />
      {hover !== null && <rect x={pad.left + slot * hover} y={pad.top} width={slot} height={plotH} fill="hsl(var(--foreground))" opacity={0.05} />}
      {data.map((day, i) => {
        let base = 0
        return <g key={day.day} className="viz-rise" style={{ animationDelay: `${Math.min(i, 40) * 18}ms` }}>
          {series.map((s, k) => {
            const v = value(day, s.key)
            if (!v) return null
            const w = mode === 'stacked' ? barW : barW / series.length
            const bx = mode === 'stacked' ? center(i) - barW / 2 : center(i) - barW / 2 + k * w
            const y0 = mode === 'stacked' ? y(base + v) : y(v)
            const h = (mode === 'stacked' ? y(base) : y(0)) - y0
            if (mode === 'stacked') base += v
            // A 2px surface gap separates stacked segments; each segment rounds only its top.
            return <rect key={s.key} x={bx} y={y0} width={Math.max(1, w - (mode === 'grouped' ? 1 : 0))} height={Math.max(0, h - (mode === 'stacked' && k ? 2 : 0))}
              rx={Math.min(3, w / 3)} fill={s.color} />
          })}
        </g>
      })}
      <rect x={pad.left} y={pad.top} width={plotW} height={plotH} fill="transparent"
        onPointerMove={pick} onPointerDown={pick} onPointerLeave={() => setHover(null)} />
    </svg>}
    {d && hover !== null && <Tooltip x={center(hover)} width={width} title={shortDay(d.day)}
      rows={series.map(s => <TooltipRow key={s.key} color={s.color} label={s.label}>{tooltipFormat(value(d, s.key))}</TooltipRow>)} />}
  </div>
}

export function Legend({ series }: { series: ChartSeries[] }) {
  return <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
    {series.map(s => <span key={s.key} className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} />{s.label}</span>)}
  </div>
}
